import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';

// Create a context for notifications
export const NotificationContext = createContext();

// Types of notifications with their styles
const notificationTypes = {
  info: { 
    icon: '💬', 
    background: '#e3f2fd', 
    color: '#0277bd',
    borderColor: '#64b5f6'
  },
  success: { 
    icon: '✅', 
    background: '#e8f5e9', 
    color: '#2e7d32',
    borderColor: '#81c784'
  },
  warning: { 
    icon: '⚠️', 
    background: '#fff8e1', 
    color: '#ff8f00',
    borderColor: '#ffd54f'
  },
  error: { 
    icon: '❌', 
    background: '#ffebee', 
    color: '#c62828',
    borderColor: '#ef5350'
  }
};

// Create a provider component
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  // Add a notification
  const addNotification = useCallback((message, type = 'info', duration = 5000, id = null) => {
    const notificationId = id || `notification-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    
    // Add the notification to the array
    setNotifications(prev => [
      ...prev,
      { id: notificationId, message, type, duration }
    ]);
    
    // Auto-remove non-error notifications after duration
    if (duration > 0 && type !== 'error') {
      setTimeout(() => {
        removeNotification(notificationId);
      }, duration);
    }
    
    return notificationId;
  }, []);

  // Remove a notification by ID
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  // Update a notification (e.g., change type from loading to success)
  const updateNotification = useCallback((id, updates) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, ...updates } 
          : notification
      )
    );
    
    // If duration is provided and it's not an error, set auto-dismiss
    if (updates.duration > 0 && updates.type !== 'error') {
      setTimeout(() => {
        removeNotification(id);
      }, updates.duration);
    }
  }, [removeNotification]);

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // The value to be provided to consumers
  const value = {
    notifications,
    addNotification,
    removeNotification,
    updateNotification,
    clearAllNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer />
    </NotificationContext.Provider>
  );
};

// Custom hook for using notifications
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

// Individual notification component
const Notification = ({ notification, onDismiss }) => {
  const { type, message, id } = notification;
  const styles = notificationTypes[type] || notificationTypes.info;
  const [isExiting, setIsExiting] = useState(false);
  
  // Handle dismiss with animation
  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(id);
    }, 300); // Match transition duration
  };
  
  // Set up keyboard accessibility
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleDismiss();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [id]);

  return (
    <div 
      role="alert"
      aria-live={type === 'error' ? 'assertive' : 'polite'}
      tabIndex={0}
      className="notification"
      style={{
        padding: '12px 16px',
        borderRadius: '6px',
        margin: '8px 0',
        display: 'flex',
        alignItems: 'center',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        backgroundColor: styles.background,
        color: styles.color,
        borderLeft: `4px solid ${styles.borderColor}`,
        maxWidth: '400px',
        width: '100%',
        position: 'relative',
        transition: 'all 0.3s ease',
        opacity: isExiting ? 0 : 1,
        transform: isExiting ? 'translateX(100%)' : 'translateX(0)',
        overflow: 'hidden',
        animation: 'notification-slide-in 0.3s ease-out'
      }}
    >
      <span style={{ marginRight: '12px', fontSize: '18px' }}>{styles.icon}</span>
      <div style={{ 
        flex: 1, 
        fontSize: '14px', 
        fontWeight: 500,
        lineHeight: '1.4'
      }}>
        {message}
      </div>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss notification"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '18px',
          marginLeft: '12px',
          color: styles.color,
          opacity: 0.7,
          transition: 'opacity 0.2s',
          padding: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ':hover': {
            opacity: 1
          }
        }}
      >
        ×
      </button>
      <style jsx>{`
        @keyframes notification-slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

// Container for all notifications
const NotificationContainer = () => {
  const { notifications, removeNotification } = useContext(NotificationContext);
  
  if (notifications.length === 0) return null;
  
  return (
    <div
      aria-label="Notifications"
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        maxHeight: '80vh',
        overflowY: 'auto',
        padding: '10px',
        pointerEvents: 'auto',
        filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))'
      }}
    >
      {notifications.map(notification => (
        <Notification
          key={notification.id}
          notification={notification}
          onDismiss={removeNotification}
        />
      ))}
    </div>
  );
};

export default NotificationProvider; 